// 入札時カバー量の「入札済み」「全札（下書き含む）」2 本の線 (AC-BIN-105 / AC-BSP-108) へ
// コマ別の買・売を足す手順。 スポット通常・スポットブロック・時間前の 3 builder が共有する
// (札をどの線に入れるかの判定だけが市場ごとに違う)。
import type { CoverLine } from './types'

/**
 * 1 本の線のコマ別の買・売の合計。 `buy` / `sell` は発注主体区分が空欄の札も含む入札量
 * (「0 件のコマか」の判定用)、 `coverBuy` / `coverSell` は空欄の札を除く入札量
 * (net 充当の入札時カバー量用)。
 */
export type KomaLineTotals = {
  buy: Map<number, number>
  sell: Map<number, number>
  coverBuy: Map<number, number>
  coverSell: Map<number, number>
}

export type KomaCoverLines = {
  all: KomaLineTotals
  submitted: KomaLineTotals
}

const createKomaLineTotals = (): KomaLineTotals => ({
  buy: new Map(),
  sell: new Map(),
  coverBuy: new Map(),
  coverSell: new Map(),
})

export const createKomaCoverLines = (): KomaCoverLines => ({
  all: createKomaLineTotals(),
  submitted: createKomaLineTotals(),
})

const addTo = (map: Map<number, number>, key: number, value: number): void => {
  map.set(key, (map.get(key) ?? 0) + value)
}

/**
 * 札が入札時カバー量に足す量 MW (AC-BIN-105 / AC-BSP-108)。 部分約定は約定した量、
 * それ以外は入札した時点の量 (入札時カバー量は約定結果で書き換えない原則の、 札が終わって
 * 残りがもう埋まらない部分約定だけの例外)。 札ごとに 1 回だけ決め、 カバー線
 * (`addToKomaCoverLines`) と区分不明の札 (`unknownIssuerBidMw`) の両方へ同じ値を渡す。
 */
export const coverBidMw = (params: {
  partialMatched: boolean
  volumeMw: number
  execVolumeMw: number | undefined
}): number =>
  params.partialMatched && params.execVolumeMw !== undefined
    ? params.execVolumeMw
    : params.volumeMw

/**
 * 札の入札量を、 札の `coverLine` が示す線に足す。 'submitted' は「全札」にも足す
 * (「入札済み」は「全札」の部分集合)。 `countsToCover` が false の札
 * (発注主体区分が空欄) は入札量 (`buy` / `sell`) にだけ足す。
 */
export const addToKomaCoverLines = (
  lines: KomaCoverLines,
  coverLine: CoverLine,
  params: {
    komaKey: number
    isSell: boolean
    volume: number
    countsToCover: boolean
  },
): void => {
  if (coverLine === 'excluded') return
  const { komaKey, isSell, volume, countsToCover } = params
  const targets =
    coverLine === 'submitted' ? [lines.all, lines.submitted] : [lines.all]
  for (const line of targets) {
    addTo(isSell ? line.sell : line.buy, komaKey, volume)
    if (countsToCover) {
      addTo(isSell ? line.coverSell : line.coverBuy, komaKey, volume)
    }
  }
}

export type KomaLineValue = {
  buyMw: number
  sellMw: number
  /** net 充当 (買 − 売)。 */
  coverMw: number
}

export const readKomaLine = (
  line: KomaLineTotals,
  komaKey: number,
): KomaLineValue => ({
  buyMw: line.buy.get(komaKey) ?? 0,
  sellMw: line.sell.get(komaKey) ?? 0,
  coverMw:
    (line.coverBuy.get(komaKey) ?? 0) - (line.coverSell.get(komaKey) ?? 0),
})
